import { IImageFeature } from '@models/IImageFeature';
import { ILightBoxImage } from '@models/ILightBoxImage';
import ImageLightBox from '@molecules/ImageLightBox/ImageLightBox';
import InViewVideo from '@molecules/InViewVideo/InViewVideo';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import classNames from 'classnames';

const getVideoOverlayClassName = (position: string) =>
  classNames('hidden lg:block absolute z-10 bg-white dark:bg-black-100', {
    'top-0 h-13 xl:h-24 w-full': position === 'top',
    'bottom-0 h-13 xl:h-24 w-full': position === 'bottom',
    'left-0 top-[-50%] h-full w-13 xl:w-24 translate-y-[50%]':
      position === 'left',
    'right-0 top-[-50%] h-full w-13 xl:w-24 translate-y-[50%]':
      position === 'right',
  });

const ImageFeature = ({
  alignment,
  mainImage,
  meta,
  secondaryMedia,
  text,
  title,
}: IImageFeature) => {
  const isVideo = !!(
    secondaryMedia &&
    'video' in secondaryMedia &&
    secondaryMedia?.video?.url
  );

  const isLeftAligned = alignment === 1;
  const isRightAligned = alignment !== 1;

  return (
    <section
      className="component-padding bg-white text-black-100 dark:bg-black-100 dark:text-white"
      data-testid="CT-36-Image-Feature"
    >
      <SoftLoadWrapper>
        <div
          className={classNames(
            isLeftAligned ? 'lg:flex-row' : 'lg:flex-row-reverse',
            'container flex flex-col lg:items-center'
          )}
        >
          <div className="block lg:hidden">
            {title && (
              <h2
                className="mb-10"
                data-epi-property-name={meta?.editHints?.title}
                data-testid="CT-36-Image-Feature-Title"
              >
                {title}
              </h2>
            )}
          </div>

          <div
            className="w-full lg:w-7/12"
            data-epi-property-name={meta?.editHints?.mainImage}
          >
            <ImageLightBox
              {...mainImage}
              imageClasses="h-full w-full"
              contentIds={
                mainImage?.meta?.contentLink
                  ? [mainImage.meta.contentLink as string]
                  : []
              }
              dataTestId="CT-36-Image-Feature-Image-LightBox"
            />
          </div>

          <div
            className={classNames(
              isLeftAligned ? 'ml-auto' : 'mr-auto',
              'my-5 w-full lg:my-0',
              isVideo ? 'lg:w-5/12' : 'lg:w-4/12'
            )}
            data-epi-property-name={meta?.editHints?.secondaryImage}
          >
            {title && (
              <h2
                className={classNames(
                  'hidden lg:block',
                  isVideo ? '-mb-5 xl:-mb-[70px] relative z-20' : 'mb-7.5',
                  {
                    'ml-13 xl:ml-24': isVideo && isLeftAligned,
                    'mr-13 xl:mr-24': isVideo && isRightAligned,
                  }
                )}
                data-testid="CT-36-Image-Feature-Title"
              >
                {title}
              </h2>
            )}

            {isVideo ? (
              <div className="relative">
                <div className={getVideoOverlayClassName('top')} />

                <div
                  className={getVideoOverlayClassName(
                    isLeftAligned ? 'left' : 'right'
                  )}
                />

                <div className={getVideoOverlayClassName('bottom')} />

                <InViewVideo
                  autoPlay
                  controls={false}
                  video={secondaryMedia.video}
                  hasInVideoPlayButton
                  videoClasses="overflow-hidden max-h-[900px] [&>div>video]:object-cover"
                  videoRatio="aspect-2/3"
                  inVideoPlayButtonPositionClassName={classNames(
                    'left-2.5 bottom-2.5 lg:bottom-15 xl:left-5 xl:bottom-[120px]',
                    isLeftAligned && 'lg:left-15 xl:left-[120px]'
                  )}
                  dataTestId="CT-36-Image-Feature-Video"
                />
              </div>
            ) : (
              <ImageLightBox
                {...(secondaryMedia as ILightBoxImage)}
                imageClasses="h-full w-full"
                contentIds={
                  secondaryMedia?.meta?.contentLink
                    ? [secondaryMedia.meta.contentLink as string]
                    : []
                }
                dataTestId="CT-36-Image-Feature-Image-LightBox"
              />
            )}

            {text && (
              <div
                className={classNames(
                  'body-two hidden lg:block',
                  isVideo ? '-mt-5 xl:-mt-[70px] relative z-20' : 'mt-7.5',
                  {
                    'ml-13 xl:ml-24': isVideo && isLeftAligned,
                    'mr-13 xl:mr-24': isVideo && isRightAligned,
                  }
                )}
                dangerouslySetInnerHTML={{
                  __html: text,
                }}
                data-testid="CT-36-Image-Feature-Text"
              />
            )}
          </div>

          <div className="block lg:hidden">
            {text && (
              <div
                className="body-two mt-5"
                data-epi-property-name={meta?.editHints?.text}
                dangerouslySetInnerHTML={{
                  __html: text,
                }}
                data-testid="CT-36-Image-Feature-Text"
              />
            )}
          </div>
        </div>
      </SoftLoadWrapper>
    </section>
  );
};

export default ImageFeature;
